// absolute imports
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { SnackbarProvider } from 'notistack';
import { lazy, Suspense } from 'react';

// relative imports
import { LedgibleAuthProvider } from '../components/providers/LedgibleAuthProvider';
import { ModalActionsProvider } from '../components/providers/ModalActionsProvider';
import CloseSnackbar from '../components/shared/custom-components/CloseSnackbar';
import NotFoundPage from '../components/shared/route-management/NotFoundPage';
import PageMeta from '../components/shared/route-management/PageMeta';
import { getAppTheme } from '../data/app-config/configs/configUtilities';
import { LedgibleRouterContext } from '../types/router';

const TanStackRouterDevtools =
  import.meta.env.mode === 'production' || window.Cypress
    ? () => null // Render nothing in production or cypress tests
    : lazy(() =>
        // Lazy load in development
        import('@tanstack/react-router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
        })),
      );

function RootComponent() {
  const appTheme = getAppTheme();

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <PageMeta />
        <Suspense>
          <TanStackRouterDevtools />
        </Suspense>
        <SnackbarProvider action={CloseSnackbar} />
        <ModalActionsProvider>
          <LedgibleAuthProvider>
            <Outlet />
          </LedgibleAuthProvider>
        </ModalActionsProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

type RootSearch = {
  msids?: String[];
  [key: string]: any;
};

export const Route = createRootRouteWithContext<LedgibleRouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundPage, // Render NotFoundPage within root component tree for provider access
  validateSearch: (search: Record<string, unknown>): RootSearch =>
    // ensure proper typing and return as is since qs is handling upstream
    search as RootSearch,
});
